import { createColumnHelper } from '@tanstack/react-table';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import ReactTable from 'app/modules/common/table';
import { formatDateWithTime } from 'app/shared/util/date-utils';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { Button, Col, Input, Row } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getEntities } from '../../repair-mode-audit-history/repair-mode-audit-history.reducer';
import { getEntities as getAuditTrail } from '../../audit-trail/audit-trail.reducer';

const PAGE_SIZE = 20;

interface IChangeFilters {
  search: string;
  from: string;
  to: string;
}
const EMPTY_FILTERS: IChangeFilters = { search: '', from: '', to: '' };

const formatDuration = (startTime, endTime) => {
  if (!startTime || !endTime) return '-';
  const diffMs = dayjs(endTime).diff(dayjs(startTime));
  const totalSeconds = Math.floor(diffMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
};

const columnHelper = createColumnHelper<any>();

const columns = [
  columnHelper.accessor('status', {
    id: 'status',
    header: 'Current State',
    cell: col => (
      <span className={`badge ${col.row.original.status === 'ON' ? 'bg-danger' : 'bg-success'}`}>
        {col.row.original.status === 'ON' ? 'Bypassed' : 'Normal'}
      </span>
    ),
  }),
  columnHelper.accessor('startTime', {
    id: 'startTime',
    header: 'Start Time',
    cell: col => formatDateWithTime(col.row.original.startTime) || '-',
  }),
  columnHelper.accessor('endTime', {
    id: 'endTime',
    header: 'End Time',
    cell: col => formatDateWithTime(col.row.original.endTime) || '-',
  }),
  columnHelper.accessor('duration', {
    id: 'duration',
    header: 'Duration',
    cell: col => formatDuration(col.row.original.startTime, col.row.original.endTime),
  }),
  columnHelper.accessor('cancelledBy', {
    id: 'cancelledBy',
    header: 'Cancellation Event',
    cell: col => (col.row.original.cancelledBy ? `Cancelled by ${col.row.original.cancelledBy}` : '-'),
  }),
];

const changeColumns = [
  columnHelper.accessor('changedAt', {
    id: 'changedAt',
    header: 'Changed At',
    cell: col => formatDateWithTime(col.row.original.changedAt) || '-',
  }),
  columnHelper.accessor('changedBy', { id: 'changedBy', header: 'Changed By', cell: col => col.row.original.changedBy || '-' }),
  columnHelper.accessor('fieldName', { id: 'fieldName', header: 'Field', cell: col => col.row.original.fieldName || '-' }),
  columnHelper.accessor('oldValue', { id: 'oldValue', header: 'Old Value', cell: col => col.row.original.oldValue ?? '-' }),
  columnHelper.accessor('newValue', { id: 'newValue', header: 'New Value', cell: col => col.row.original.newValue ?? '-' }),
];

export const DeviceAuditTrail = ({ refreshTrigger }: { refreshTrigger?: number }) => {
  const dispatch = useAppDispatch();
  const { id } = useParams<'id'>();

  const auditHistory = useAppSelector(state => state.repairModeAuditHistory.entities);
  const loading = useAppSelector(state => state.repairModeAuditHistory.loading);

  const changes = useAppSelector(state => state.auditTrail.entities);
  const changesLoading = useAppSelector(state => state.auditTrail.loading);
  const changesTotal = useAppSelector(state => state.auditTrail.totalItems);
  const [activePage, setActivePage] = useState(1);
  const [draftFilters, setDraftFilters] = useState<IChangeFilters>(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<IChangeFilters>(EMPTY_FILTERS);

  const getChanges = (page = activePage, filters = appliedFilters) => {
    dispatch(
      getAuditTrail({
        entityName: 'Device',
        entityId: id,
        search: filters.search.trim(),
        from: filters.from ? dayjs(filters.from).startOf('day').toISOString() : undefined,
        to: filters.to ? dayjs(filters.to).endOf('day').toISOString() : undefined,
        page: page - 1,
        size: PAGE_SIZE,
      }),
    );
  };

  const applyFilters = () => {
    setAppliedFilters(draftFilters);
    if (activePage === 1) {
      getChanges(1, draftFilters);
    } else {
      setActivePage(1); // the activePage effect reloads using the applied filters
    }
  };

  const resetFilters = () => {
    setDraftFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    if (activePage === 1) {
      getChanges(1, EMPTY_FILTERS);
    } else {
      setActivePage(1);
    }
  };

  const updateDraft = (key: keyof IChangeFilters) => e => setDraftFilters(prev => ({ ...prev, [key]: e.target.value }));

  const getLatestEntities = () => {
    getChanges();
    dispatch(
      getEntities({
        filterUrl: `deviceId.equals=${id}&sort=id,desc&`,
      }),
    );
  };

  useEffect(() => {
    getLatestEntities();
  }, [id, refreshTrigger]);

  useEffect(() => {
    getChanges();
  }, [activePage]);

  return (
    <div className="audit-trail-tab">
      <div className="d-flex justify-content-end">
        <Button color="info" className="me-2" onClick={getLatestEntities} style={{ pointerEvents: loading ? 'none' : 'all' }}>
          <FontAwesomeIcon icon="sync" spin={loading} /> Refresh List
        </Button>
      </div>
      <h5>Change History</h5>
      <Row className="g-2 mb-3 align-items-end">
        <Col md={5}>
          <Input
            type="text"
            placeholder="Search field, old/new value or user"
            value={draftFilters.search}
            onChange={updateDraft('search')}
            onKeyDown={e => e.key === 'Enter' && applyFilters()}
            data-cy="auditTrailSearch"
          />
        </Col>
        <Col md={2}>
          <Input
            type="date"
            value={draftFilters.from}
            max={draftFilters.to || undefined}
            onChange={updateDraft('from')}
            title="From date"
          />
        </Col>
        <Col md={2}>
          <Input type="date" value={draftFilters.to} min={draftFilters.from || undefined} onChange={updateDraft('to')} title="To date" />
        </Col>
        <Col md={3} className="d-flex gap-2">
          <Button color="primary" onClick={applyFilters} data-cy="auditTrailSearchButton">
            <FontAwesomeIcon icon="search" /> Search
          </Button>
          <Button color="secondary" outline onClick={resetFilters}>
            Reset
          </Button>
        </Col>
      </Row>
      <ReactTable
        columns={changeColumns}
        data={changesLoading ? [] : changes}
        isLoading={changesLoading}
        sortingDisabled
        totalItems={changesTotal}
        paginationState={{ activePage, itemsPerPage: PAGE_SIZE }}
        handlePagination={setActivePage}
      />
      <h5 className="mt-4">Repair Mode History</h5>
      <ReactTable columns={columns} data={loading ? [] : auditHistory} isLoading={loading} />
    </div>
  );
};

export default DeviceAuditTrail;
